import { Injectable, NgZone, signal } from '@angular/core';
import { EventEmitter2 } from 'eventemitter2';
import { JackpotRoom } from './jackpot-room';
import { ModalService } from '@app/modal/modal.service';
import { environment } from '@env/environment';
import { generateJackpotRound } from '@features/demo/jackpot';
import { Item } from '@models/item.interface';
import { JackpotBet } from '@models/jackpot-bet.interface';
import { Jackpot } from '@models/jackpot.interface';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { UIService } from '@shared/ui.service';

@Injectable({
  providedIn: 'root',
})
export class JackpotService {
  room: JackpotRoom;
  round = signal<Jackpot>({
    bets: [],
    value: 0,
    gameid: 0,
    hash: '',
    potid: 1,
    status: 'waiting',
  });
  history = signal<Jackpot[]>([]);
  events: EventEmitter2 = new EventEmitter2();
  minBet = 0.15;
  maxBet = 5000;
  minItems = 1;
  maxItems = 15;
  sessionChance = 0;
  sessionDepositValue = 0;
  timer = signal(30);
  maxTimer = 30;
  totalValue = signal(0);
  groupedBets = signal<JackpotBet[]>([]);

  constructor(private socketService: SocketService, private session: SessionService, private uiService: UIService, private ngZone: NgZone, private modalService: ModalService) {
    this.room = new JackpotRoom(this.socketService, 1);

    this.room.events.on('update_round', (data: Jackpot) => {
      if (this.round().status === 'rolling') {
        return;
      }

      this.round.set(data);
      this.groupedBets.set(this.groupUserBets(data.bets));
      this.sessionChance = this.getSessionChance();
      this.sessionDepositValue = this.getSessionDepositValue();
      this.totalValue.set(this.round().bets.reduce((acc, cur) => acc + cur.total_value, 0));
      if (this.round().status === 'running') {
        this.maxTimer = this.round().end - this.round().start;
        this.timer.set(this.round().end - Math.round(Date.now() / 1000));
      } else if (this.round().status === 'rolling') {
        this.maxTimer = this.round().roll_end - this.round().end;
        this.timer.set(this.round().roll_end - Math.round(Date.now() / 1000));
      } else if (this.round().status === 'awaiting') {
        this.timer.set(30);
        this.maxTimer = 30;
      }

      if (data.bets.some((bet) => bet.steamid === this.session.profile()?.steamid) && !this.round()?.bets.some((bet) => bet.steamid === this.session.profile()?.steamid)) {
        this.modalService.close();
      }

      this.events.emit('update_round', data);
    });

    this.room.events.on('end_jackpot', (data) => {
      this.round.set(data);
      this.groupedBets.set(this.groupUserBets(data.bets));
      this.maxTimer = 100;
      this.timer.set(0);
      this.maxTimer = this.round().roll_end - this.round().end;
      this.timer.set(this.round().roll_end - Math.round(new Date().getTime() / 1000));
      // this.events.emit('end_jackpot', data);
      this.uiService.playSound('jp-roll', { maxVolume: 0.5, routeCondition: 'jackpot' });
      this.totalValue.set(this.round().bets.reduce((acc, cur) => acc + cur.total_value, 0));

      setTimeout(() => {
        this.round.update((currentRound) => ({
          ...currentRound,
          status: 'finished',
        }));
        this.totalValue.set(0);
      }, this.timer() * 1000 + 3000);
    });

    this.room.events.on('show_history', (data) => {
      this.history.set(data.history.reverse().slice(0, 5));
    });

    if (environment.loadDemo) {
      this.round.set(generateJackpotRound(true));
      this.history.set([generateJackpotRound(true), generateJackpotRound(true), generateJackpotRound(true), generateJackpotRound(true)]);
      this.sessionChance = this.getSessionChance();
      this.sessionDepositValue = this.getSessionDepositValue();
      this.groupedBets.set(this.groupUserBets(this.round().bets));
    }

    setInterval(() => {
      if (this.timer() > 0 && this.round().status === 'running') {
        this.timer.update((t) => t - 1);
      }
    }, 1000);
  }

  getSessionChance(): number {
    const steamid = this.session?.profile()?.steamid || '0';
    const bets = this.round().bets.filter((bet) => bet.steamid === steamid);
    const totalChance = bets.reduce((acc, cur) => acc + cur.chances, 0);
    return bets.length > 0 ? totalChance : 0;
  }

  getSessionDepositValue(): number {
    const steamid = this.session?.profile()?.steamid || '0';
    const bets = this.round().bets.filter((bet) => bet.steamid === steamid);
    const totalValue = bets.reduce((acc, cur) => acc + cur.total_value, 0);
    return bets.length > 0 ? totalValue : 0;
  }

  async joinJackpot(items: Item[]) {
    return await this.socketService.emit('jackpot_deposit', { skins: items });
  }

  groupUserBets(bets: JackpotBet[]): JackpotBet[] {
    if (!(bets.length > 0)) {
      return [];
    }

    const userBetsMap = new Map<string, JackpotBet>();

    for (const bet of bets) {
      const userId = bet.id.toString();
      if (userBetsMap.has(userId)) {
        const existingBet = userBetsMap.get(userId)!;
        existingBet.total_value += bet.total_value;
        existingBet.chances += bet.chances;
        existingBet.skins = existingBet.skins.concat(bet.skins || []);
      } else {
        userBetsMap.set(userId, { ...bet, skins: bet.skins || [] });
      }
    }

    const userBets = Array.from(userBetsMap.values());
    const totalValue = userBets.reduce((acc, bet) => acc + bet.total_value, 0);

    let rawTotalChance = 0;
    userBets.forEach((bet) => {
      bet.chances = (bet.total_value / totalValue) * 100;
      rawTotalChance += bet.chances;
    });

    const roundingDifference = 100 - rawTotalChance;
    if (roundingDifference !== 0) {
      userBets[0].chances += roundingDifference;
    }

    return userBets;
  }
}
